import { translateMessage } from "@/i18n";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";
export type ButtonSize = "sm" | "md" | "lg" | "icon";
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  isLoading?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
}
const variants: Record<ButtonVariant, string> = {
  primary:
    "border-ink bg-ink text-surface shadow-sm hover:-translate-y-0.5 hover:border-accent hover:bg-accent hover:shadow-card disabled:hover:translate-y-0 disabled:hover:border-ink disabled:hover:bg-ink disabled:hover:shadow-sm",
  secondary:
    "border-line bg-surface text-ink shadow-sm hover:-translate-y-0.5 hover:border-accent hover:bg-accent/10 hover:shadow-card disabled:hover:translate-y-0 disabled:hover:border-line disabled:hover:bg-surface disabled:hover:shadow-sm",
  quiet:
    "border-transparent bg-transparent text-ink hover:border-accent/20 hover:bg-accent/10",
  danger:
    "border-danger bg-danger text-white shadow-sm hover:-translate-y-0.5 hover:brightness-90 hover:shadow-card",
};
const sizes: Record<ButtonSize, string> = {
  sm: "min-h-9 px-3 text-sm",
  md: "min-h-11 px-4 text-sm",
  lg: "min-h-12 px-5 text-base",
  icon: "h-11 w-11 p-0",
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      disabled,
      isLoading = false,
      size = "md",
      type = "button",
      variant = "primary",
      ...props
    },
    ref
  ) => (
    <button
      className={twMerge(
        clsx(
          "inline-flex flex-nowrap items-center justify-center gap-2 whitespace-nowrap rounded-lg border font-semibold transition-[transform,box-shadow,background-color,border-color,color] duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-55",
          variants[variant],
          sizes[size],
          className
        )
      )}
      disabled={disabled || isLoading}
      ref={ref}
      type={type}
      {...props}
    >
      {isLoading && (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
        />
      )}
      <span className={isLoading ? "sr-only" : "inline-flex items-center gap-2"}>
        {children}
      </span>
      {isLoading && (
        <span className="sr-only">{translateMessage("common.Button.loading")}</span>
      )}
    </button>
  )
);
Button.displayName = "Button";
